<template>
  <div>
    <div class="d-flex pb-4">
      <div
        class="activity-icon rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-3"
        :class="`activity-icon--${icon.tone}`"
      >
        <i
          class="fa"
          :class="icon.name"
        />
      </div>

      <div class="flex-grow-1 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between">
          <div class="pr-3 flex-grow-1 overflow-hidden">
            <div
              v-if="headline"
              class="activity-item__title font-w600"
            >
              {{ headline }}
            </div>

            <div
              v-if="subtext"
              class="activity-item__summary text-muted font-size-md mt-1"
            >
              {{ subtext }}
            </div>

            <div
              v-if="photoThumbnails.length"
              class="activity-item__photos d-flex flex-wrap mt-2"
            >
              <button
                v-for="(photo, index) in photoThumbnails"
                :key="photo.id"
                type="button"
                class="activity-item__photo-link mr-2 mb-2"
                @click="openPhoto(index)"
              >
                <img
                  class="activity-item__photo-thumb"
                  :src="photo.thumbnail_url || photo.url"
                  :alt="headline"
                >
              </button>
              <button
                v-if="extraPhotoCount"
                type="button"
                class="activity-item__photo-more d-inline-flex align-items-center justify-content-center mr-2 mb-2 font-size-sm font-w600"
                :aria-label="`View all ${photos.length} photos`"
                @click="expandPhotos"
              >
                +{{ extraPhotoCount }}
              </button>
            </div>

            <div class="activity-item__actor mt-2 d-flex flex-wrap align-items-center">
              <user-avatar-badge
                :is-system="isSystem"
                :name="actorName"
                margin-class="mr-2"
                system-aria-label="System activity"
                class="activity-item__avatar"
              />
              <b-link
                v-if="value.user"
                :to="{ name: 'user', params: { id: value.user.id } }"
                class="activity-item__actor-name font-size-sm mr-1"
              >
                {{ actorName }}
              </b-link>
              <span
                v-else
                class="font-size-sm mr-1"
              >
                {{ actorName }}
              </span>
              <span
                v-if="actorSecondary"
                class="text-muted font-size-sm"
              >
                {{ actorSecondary }}
              </span>
            </div>
          </div>

          <div
            class="text-muted font-size-sm flex-shrink-0 text-nowrap"
            v-b-tooltip.hover
            :title="value.occurred_at | dateTimeFormat"
          >
            {{ time }}
          </div>
        </div>
      </div>
    </div>

    <timeline-item-photo-modal
      v-if="photos.length"
      ref="photoModal"
      :photos="photos"
      :title="headline"
      :modal-id="photoModalId"
    />
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import UserAvatarBadge from '@/components/shared/users/UserAvatarBadge.vue';
import TimelineItemPhotoModal from '@/components/admin/workOrderActivities/TimelineItemPhotoModal.vue';
import {
  WORK_ORDER_ACTIVITY_FILTER_CATEGORIES,
  WORK_ORDER_ACTIVITY_KIND_ICONS,
} from '@/constants/workOrderActivities';

const PHOTO_FILTER_ICON = WORK_ORDER_ACTIVITY_FILTER_CATEGORIES.find((category) => category.key === 'photos')?.icon
  || 'fa-image';
const MAX_VISIBLE_PHOTO_THUMBNAILS = 4;

export default {
  name: 'WorkOrderActivitiesTimelineItem',
  components: {
    UserAvatarBadge,
    TimelineItemPhotoModal,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      photosExpanded: false,
    };
  },
  computed: {
    isSystem() {
      return !this.value.user;
    },
    actorName() {
      if (this.value.user?.name) {
        return this.value.user.name;
      }
      return this.value.source === 'integration' ? 'Integration' : 'System';
    },
    // Technician role for users; integration name for integration activities.
    // Filters applied here, not in the template, so integration casing survives (ServiceChannel).
    actorSecondary() {
      if (!this.value.user) {
        return this.value.metadata?.integration || null;
      }
      if (!['technician', 'sub_technician'].includes(this.value.user.category)) {
        return null;
      }
      return this.$options.filters.capitalizeFormat(this.$options.filters.humanizedFormat(this.value.user.category));
    },
    // Mockup: bold headline. Prefer title; fall back to summary.
    headline() {
      return this.value.title || this.value.summary;
    },
    // Show summary under the title when both exist and differ.
    subtext() {
      if (!this.value.title || !this.value.summary) {
        return null;
      }
      if (this.value.title === this.value.summary) {
        return null;
      }
      return this.value.summary;
    },
    icon() {
      if (this.value.category === 'photos' || String(this.value.kind || '').startsWith('photo_')) {
        return { name: PHOTO_FILTER_ICON, tone: 'info' };
      }

      const config = WORK_ORDER_ACTIVITY_KIND_ICONS[this.value.kind];
      return {
        name: config?.icon || 'fa-circle',
        tone: config?.tone || 'secondary',
      };
    },
    time() {
      return DateTime.fromISO(this.value.occurred_at).toLocaleString(DateTime.TIME_SIMPLE);
    },
    photos() {
      return this.value.photos || [];
    },
    photoThumbnails() {
      if (this.photosExpanded) {
        return this.photos;
      }
      return this.photos.slice(0, MAX_VISIBLE_PHOTO_THUMBNAILS);
    },
    extraPhotoCount() {
      if (this.photosExpanded) {
        return 0;
      }
      return this.photos.length > MAX_VISIBLE_PHOTO_THUMBNAILS
        ? this.photos.length - MAX_VISIBLE_PHOTO_THUMBNAILS
        : 0;
    },
    photoModalId() {
      return `timeline-item-photo-modal-${this.value.id}`;
    },
  },
  methods: {
    openPhoto(index) {
      this.$refs.photoModal?.openPhoto(index);
    },
    expandPhotos() {
      this.photosExpanded = true;
    },
  },
};
</script>

<style scoped>
.activity-icon {
  width: 2rem;
  height: 2rem;
  font-size: 0.75rem;
  position: relative;
  z-index: 1;
  border: 2px solid #fff;
  outline: 1px solid #e9ecef;
}

/* Soft pastel markers from the mockup — Bootstrap has no soft-* utilities here. */
.activity-icon--primary { background: #e7f1fb; color: #3c90df; }
.activity-icon--success { background: #e6f4ea; color: #28a745; }
.activity-icon--warning { background: #fff3cd; color: #d39e00; }
.activity-icon--danger { background: #f8d7da; color: #dc3545; }
.activity-icon--info { background: #e8f6f8; color: #17a2b8; }
.activity-icon--secondary { background: #e9ecef; color: #6c757d; }

.activity-item__title {
  color: #343a40;
  line-height: 1.35;
}

.activity-item__avatar {
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.65rem;
}

.activity-item__photo-link {
  display: block;
  line-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.activity-item__photo-link:hover .activity-item__photo-thumb {
  opacity: 0.85;
}

.activity-item__photo-thumb {
  width: 4rem;
  height: 4rem;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.activity-item__photo-more {
  width: 4rem;
  height: 4rem;
  padding: 0;
  border-radius: 0.375rem;
  border: 1px dashed #ced4da;
  background: #f8f9fa;
  color: #6c757d;
  cursor: pointer;
}

.activity-item__photo-more:hover {
  background: #eef4fb;
  border-color: #3c90df;
  color: #3c90df;
}
</style>
